import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import { getPlanInfo } from '../utils/plan';
import { useAuth } from '../contexts/AuthContext';

const SCREENSHOT_INTERVALS_PAID = [1, 5, 10, 15, 30, 60, 120, 180];
const SCREENSHOT_INTERVALS_FREE = [30, 60, 120, 180];
const EMOJIS = ['👦', '👧', '🧒', '🧑', '👶'];

/* ── Reusable components ─────────────────────────────────────────────────── */

function Section({ title, accent, children }) {
  return (
    <div style={{
      background: '#fff', borderRadius: 14, padding: '22px 24px', marginBottom: 18,
      boxShadow: '0 1px 4px rgba(0,0,0,.06)',
      border: '1px solid #e2e8f0',
    }}>
      <div style={{
        fontSize: 14, fontWeight: 700, color: '#1e293b', marginBottom: 16,
        display: 'flex', alignItems: 'center', gap: 8,
      }}>{title}</div>
      {children}
    </div>
  );
}

function Toggle({ checked, onChange, label, disabled }) {
  return (
    <label style={{ display: 'flex', alignItems: 'center', gap: 10,
      cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.5 : 1 }}>
      <div onClick={() => !disabled && onChange(!checked)}
        style={{
          width: 44, height: 24, borderRadius: 12,
          background: checked ? '#0ea5e9' : '#cbd5e1',
          position: 'relative', cursor: disabled ? 'not-allowed' : 'pointer',
          flexShrink: 0, transition: 'background 0.2s',
        }}>
        <div style={{
          position: 'absolute', top: 3, left: checked ? 23 : 3,
          width: 18, height: 18, borderRadius: '50%', background: '#fff',
          transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
        }} />
      </div>
      <span style={{ fontSize: 13, color: '#374151' }}>{label}</span>
    </label>
  );
}

function LockedRow({ title, desc, navigate }) {
  return (
    <div style={{
      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      flexWrap: 'wrap', gap: 8,
      padding: '12px 14px', background: '#fffbeb',
      border: '1px solid #fde68a', borderRadius: 8, marginBottom: 8,
    }}>
      <div>
        <div style={{ fontSize: 13, fontWeight: 600, color: '#374151' }}>{title}</div>
        <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 2 }}>{desc}</div>
        <span style={{
          display: 'inline-block', marginTop: 4,
          background: '#fef3c7', color: '#92400e',
          padding: '2px 8px', borderRadius: 99, fontSize: 10, fontWeight: 700,
        }}>⭐ Plus Feature</span>
      </div>
      <button className="btn"
        style={{ background: 'linear-gradient(135deg,#f59e0b,#d97706)',
          marginTop: 0, fontSize: 12, padding: '7px 14px', whiteSpace: 'nowrap' }}
        onClick={() => navigate('/upgrade')}>
        Unlock →
      </button>
    </div>
  );
}

function Pill({ label, color, onRemove }) {
  return (
    <span style={{
      background: color || '#fee2e2', borderRadius: 99,
      padding: '5px 12px', fontSize: 12, fontWeight: 500,
      display: 'inline-flex', alignItems: 'center', gap: 6,
    }}>
      {label}
      {onRemove && (
        <button onClick={onRemove}
          style={{ border: 'none', background: 'none', cursor: 'pointer',
            fontSize: 14, padding: 0, lineHeight: 1, opacity: 0.6 }}>×</button>
      )}
    </span>
  );
}

/* ── Main component ──────────────────────────────────────────────────────── */

export default function Settings({ onNameChange, onPlanChange, kidId, parentId, deviceFilter }) {
  const navigate = useNavigate();
  const { kids: kidsFromAuth, customer } = useAuth() || { kids: [], customer: null };

  const [settingsId,        setSettingsId]        = useState(null);
  const [childName,         setChildName]          = useState('Your Child');
  const [avatar,            setAvatar]             = useState('👦');
  const [dailyLimit,        setDailyLimit]         = useState(180);
  const [bedtimeEnabled,    setBedtimeEnabled]     = useState(true);
  const [bedtimeHour,       setBedtimeHour]        = useState(22);
  const [waketimeHour,      setWaketimeHour]       = useState(7);
  const [blockedSites,      setBlockedSites]       = useState([]);
  const [newSite,           setNewSite]            = useState('');
  const [customKeywords,    setCustomKeywords]     = useState([]);
  const [newKeyword,        setNewKeyword]         = useState('');
  const [alertWhatsapp,     setAlertWhatsapp]      = useState('');
  const [alertEmail,        setAlertEmail]         = useState('');
  const [screenshotEnabled, setScreenshotEnabled]  = useState(true);
  const [ssInterval,        setSsInterval]         = useState(5);
  const [ssQuality,         setSsQuality]          = useState('medium');
  const [hideIcon,          setHideIcon]           = useState(false);
  const [saving,            setSaving]             = useState(false);
  const [toast,             setToast]              = useState('');
  const [loading,           setLoading]            = useState(true);
  const [confirmClear,      setConfirmClear]       = useState(null);
  const [plan,              setPlan]               = useState('trial');
  const [planInfo,          setPlanInfo]           = useState({ isPaid: true, isTrial: true });
  const [setupCode,         setSetupCode]         = useState(null);
  const [codeLoading,       setCodeLoading]       = useState(false);
  const [devices,           setDevices]           = useState([]);
  const [lockScreenEnabled, setLockScreenEnabled] = useState(false);
  const [bedtimePin,        setBedtimePin]        = useState('1234');
  const [lockActive,        setLockActive]        = useState(false);

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(''), 3000); };

  // Lock settings are stored as special _pg: prefixed entries in custom_keywords
  // e.g. ['_pg:pin:1234', '_pg:lock:true', '_pg:unlock:2024-01-01T00:00:00Z', 'actual keyword']
  const PG_PREFIX = '_pg:';
  const parsePgMeta = (keywords) => {
    const meta = {};
    (keywords || []).forEach(k => {
      if (k.startsWith(PG_PREFIX)) {
        const rest = k.slice(PG_PREFIX.length);
        const idx = rest.indexOf(':');
        if (idx > 0) meta[rest.slice(0, idx)] = rest.slice(idx + 1);
      }
    });
    return meta;
  };
  const stripPgMeta = (keywords) => (keywords || []).filter(k => !k.startsWith(PG_PREFIX));
  const buildPgMeta = (userKeywords, pin, lockEnabled, unlockUntil, quality, hideIconVal) => {
    const meta = [
      `${PG_PREFIX}pin:${pin || '1234'}`,
      `${PG_PREFIX}lock:${lockEnabled ? 'true' : 'false'}`,
      `${PG_PREFIX}ssquality:${quality || 'medium'}`,
      `${PG_PREFIX}hideicon:${hideIconVal ? 'true' : 'false'}`,
    ];
    if (unlockUntil) meta.push(`${PG_PREFIX}unlock:${unlockUntil}`);
    return [...stripPgMeta(userKeywords), ...meta];
  };

  /* ── Load from Supabase ────────────────────────────────────────────────── */
  useEffect(() => {
    let didTimeout = false;
    const timer = setTimeout(() => { didTimeout = true; setLoading(false); }, 8000);

    (async () => {
      try {
        if (!parentId) { clearTimeout(timer); setLoading(false); return; }
        // Load via serverless API to bypass RLS
        const { data: { session } } = await supabase.auth.getSession();
        const token = session?.access_token;
        const apiBase = window.location.origin;
        const resp = await fetch(`${apiBase}/api/load-settings`, {
          headers: { 'Authorization': `Bearer ${token}` },
        });
        const result = await resp.json();
        if (didTimeout) return;
        clearTimeout(timer);
        if (!resp.ok) console.error('[Settings] load error:', result.error);
        const data = result.settings ? [result.settings] : [];
        setDevices(result.devices || []);
        if (data && data[0]) {
          const s = data[0];
          const info = getPlanInfo(customer);
          setSettingsId(s.id);
          setChildName(s.child_name || 'Your Child');
          setAvatar(localStorage.getItem('pg_avatar') || '👦');
          setDailyLimit(s.daily_limit_minutes || 180);
          setBedtimeEnabled(s.bedtime_enabled !== false);
          setBedtimeHour(s.bedtime_hour ?? 22);
          setWaketimeHour(s.waketime_hour ?? 7);
          setBlockedSites(s.blocked_websites || []);
          // Extract lock settings from _pg: meta entries
          const allKw = s.custom_keywords || [];
          const meta = parsePgMeta(allKw);
          setCustomKeywords(stripPgMeta(allKw));
          setAlertWhatsapp(s.alert_whatsapp || '');
          setAlertEmail(s.alert_email || '');
          setScreenshotEnabled(s.screenshots_enabled !== false);
          setSsInterval(s.screenshot_interval || 5);
          setHideIcon(false);
          setLockScreenEnabled(meta.lock === 'true');
          setBedtimePin(meta.pin || '1234');
          if (meta.ssquality) setSsQuality(meta.ssquality);
          if (meta.hideicon) setHideIcon(meta.hideicon === 'true');
          // Check if lock is currently active
          if (meta.lock === 'true' && s.bedtime_enabled !== false) {
            const now = new Date();
            const hour = now.getHours();
            const bt = s.bedtime_hour ?? 22;
            const wt = s.waketime_hour ?? 7;
            const isBedtime = bt > wt ? (hour >= bt || hour < wt) : (hour >= bt && hour < wt);
            const unlockUntil = meta.unlock ? new Date(meta.unlock) : null;
            const unlocked = unlockUntil && new Date() < unlockUntil;
            setLockActive(isBedtime && !unlocked);
          }
          setPlan(info.plan || 'trial');
          setPlanInfo(info);
          localStorage.setItem('pg_child_name', s.child_name || 'Your Child');
          onNameChange?.(s.child_name || 'Your Child');
          onPlanChange?.(info.plan || 'trial');
        }
      } catch (e) {
        console.error('[Settings] load exception:', e);
      }
      if (!didTimeout) { clearTimeout(timer); setLoading(false); }
    })();
    return () => clearTimeout(timer);
  }, [parentId, customer]); // eslint-disable-line

  /* ── Save to Supabase ──────────────────────────────────────────────────── */
  const withTimeout = (promise, ms = 6000) =>
    Promise.race([promise, new Promise((_, rej) => setTimeout(() => rej(new Error('Request timed out')), ms))]);

  const save = async () => {
    setSaving(true);
    // Merge lock settings + screenshot quality into custom_keywords as _pg: meta
    const mergedKeywords = buildPgMeta(customKeywords, bedtimePin, lockScreenEnabled, null, ssQuality, hideIcon);
    const payload = {
      child_name: childName, daily_limit_minutes: dailyLimit,
      bedtime_enabled: bedtimeEnabled, bedtime_hour: bedtimeHour,
      waketime_hour: waketimeHour, blocked_websites: blockedSites,
      custom_keywords: mergedKeywords, alert_whatsapp: alertWhatsapp,
      alert_email: alertEmail, screenshots_enabled: screenshotEnabled,
      screenshot_interval: ssInterval,
    };
    try {
      // Use serverless API to bypass RLS
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;
      if (!token) throw new Error('Not logged in');

      const apiBase = window.location.origin;
      const resp = await fetch(`${apiBase}/api/save-settings`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });
      const text = await resp.text();
      let result;
      try { result = JSON.parse(text); } catch { throw new Error('Server returned invalid response'); }
      if (!resp.ok) throw new Error(result.error || 'Save failed');
      if (result.data?.id) setSettingsId(result.data.id);

      localStorage.setItem('pg_child_name', childName);
      localStorage.setItem('pg_avatar', avatar);
      onNameChange?.(childName || 'Your Child');
      showToast('Settings saved!');
    } catch (e) {
      console.error('[Settings] save error:', e);
      showToast('Save failed: ' + e.message);
    }
    setSaving(false);
  };

  // Helper to set unlock-until via _pg:unlock meta in custom_keywords
  const setUnlockUntil = async (minutesFromNow) => {
    try {
      const until = new Date(Date.now() + minutesFromNow * 60 * 1000).toISOString();
      const mergedKeywords = buildPgMeta(customKeywords, bedtimePin, lockScreenEnabled, until);
      await withTimeout(
        supabase.from('settings').update({ custom_keywords: mergedKeywords }).eq('id', settingsId).select()
      );
      setLockActive(false);
      showToast(minutesFromNow >= 480 ? 'Screen unlocked until wake time' : `${minutesFromNow} min of access granted`);
    } catch {
      showToast('Failed to unlock');
    }
  };

  /* ── Helpers ───────────────────────────────────────────────────────────── */
  const addSite = () => {
    const s = newSite.trim().replace(/^https?:\/\/(www\.)?/, '').split('/')[0];
    if (s && !blockedSites.includes(s)) setBlockedSites(prev => [...prev, s]);
    setNewSite('');
  };
  const addKeyword = () => {
    const k = newKeyword.trim().toLowerCase();
    if (k && !customKeywords.includes(k)) setCustomKeywords(prev => [...prev, k]);
    setNewKeyword('');
  };

  const clearData = async (type) => {
    if (!parentId) { showToast('Not logged in'); return; }
    try {
      if (type === 'screenshots') {
        const { data: all } = await supabase.from('screenshots').select('image_url').eq('parent_id', parentId);
        if (all?.length) {
          const paths = all.map(r => r.image_url);
          for (let i = 0; i < paths.length; i += 100)
            await supabase.storage.from('parentguard').remove(paths.slice(i, i + 100));
        }
        await supabase.from('screenshots').delete().eq('parent_id', parentId);
        showToast('All screenshots cleared');
      } else if (type === 'websites') {
        await supabase.from('websites').delete().eq('parent_id', parentId);
        showToast('Website history cleared');
      } else if (type === 'alerts') {
        await supabase.from('alerts').delete().eq('parent_id', parentId);
        showToast('All alerts cleared');
      }
    } catch (e) {
      showToast('Clear failed: ' + e.message);
    }
    setConfirmClear(null);
  };

  const exportJSON = async () => {
    if (!parentId) { showToast('Not logged in'); return; }
    const [ss, apps, sites, alerts] = await Promise.all([
      supabase.from('screenshots').select('*').eq('parent_id', parentId).order('timestamp', { ascending: false }),
      supabase.from('app_usage').select('*').eq('parent_id', parentId).order('timestamp', { ascending: false }),
      supabase.from('websites').select('*').eq('parent_id', parentId).order('timestamp', { ascending: false }),
      supabase.from('alerts').select('*').eq('parent_id', parentId).order('timestamp', { ascending: false }),
    ]);
    const blob = new Blob([JSON.stringify({
      exported_at: new Date().toISOString(),
      screenshots: ss.data, app_usage: apps.data,
      websites: sites.data, alerts: alerts.data,
    }, null, 2)], { type: 'application/json' });
    const a = document.createElement('a');
    const url = URL.createObjectURL(blob);
    a.href = url;
    a.download = `ParentsProtection_Export_${new Date().toISOString().slice(0,10)}.json`;
    a.click();
    URL.revokeObjectURL(url);
    showToast('Data exported');
  };

  const fmt = (h) => {
    const suffix = h >= 12 ? 'PM' : 'AM';
    const h12 = h % 12 === 0 ? 12 : h % 12;
    return `${h12}:00 ${suffix}`;
  };
  const limitLabel = dailyLimit >= 60
    ? `${Math.floor(dailyLimit / 60)}h ${dailyLimit % 60 > 0 ? dailyLimit % 60 + 'm' : ''}`
    : `${dailyLimit} min`;

  const { isPaid, isTrial } = planInfo;
  const canAccessPaid = isPaid || isTrial;

  // Device status
  const connectedDevices = devices.filter(d => d.device_name);
  const primaryDevice = connectedDevices[0];
  const isOnline = primaryDevice?.last_seen_at &&
    (Date.now() - new Date(primaryDevice.last_seen_at).getTime()) < 5 * 60 * 1000;

  /* ── Loading state ─────────────────────────────────────────────────────── */
  if (loading) return (
    <div className="loading-container">
      <div className="loading-icon">{'\u2699\uFE0F'}</div>
      <div className="loading-title">Loading settings...</div>
      <div className="loading-bar-track">
        <div className="loading-bar-fill" style={{ width: '50%' }} />
      </div>
      <div className="loading-percent">50%</div>
    </div>
  );

  /* ── Main render ───────────────────────────────────────────────────────── */
  return (
    <div style={{ maxWidth: 820, margin: '0 auto' }}>

      {/* ── Header row ─────────────────────────────────────────────────────── */}
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        marginBottom: 20, flexWrap: 'wrap', gap: 12,
      }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 800, color: '#0f172a', margin: 0 }}>Settings</h1>
          <p style={{ fontSize: 12, color: '#94a3b8', marginTop: 2 }}>
            Manage monitoring, bedtime, and device settings
          </p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{
            background: plan === 'family' ? '#7c3aed' : plan === 'plus' ? '#2563eb'
              : plan === 'trial' ? '#d97706' : '#6b7280',
            color: '#fff', padding: '5px 14px', borderRadius: 99,
            fontSize: 11, fontWeight: 700,
          }}>
            {plan === 'free' ? 'Free Plan' : plan === 'trial' ? '7-Day Trial' :
             plan === 'plus' ? 'Plus' : 'Family'}
          </span>
          <button className="btn" onClick={save} disabled={saving}
            style={{ marginTop: 0, fontSize: 13, padding: '9px 24px', fontWeight: 700 }}>
            {saving ? 'Saving...' : 'Save Settings'}
          </button>
        </div>
      </div>

      {/* ── Device Connection Banner ───────────────────────────────────────── */}
      {connectedDevices.length > 0 ? (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 14,
          padding: '16px 20px', marginBottom: 20, borderRadius: 14,
          background: isOnline
            ? 'linear-gradient(135deg, #f0fdf4, #dcfce7)'
            : 'linear-gradient(135deg, #fef2f2, #fee2e2)',
          border: `1.5px solid ${isOnline ? '#86efac' : '#fecaca'}`,
        }}>
          <div style={{
            width: 44, height: 44, borderRadius: 12,
            background: isOnline ? '#22c55e' : '#ef4444',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 20, flexShrink: 0,
          }}>
            {isOnline ? '🟢' : '🔴'}
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: isOnline ? '#166534' : '#991b1b' }}>
              {primaryDevice.device_name} is {isOnline ? 'online' : 'offline'}
            </div>
            <div style={{ fontSize: 12, color: isOnline ? '#16a34a' : '#dc2626', marginTop: 2 }}>
              {isOnline
                ? 'Agent is running and monitoring is active'
                : `Last seen: ${primaryDevice.last_seen_at
                    ? new Date(primaryDevice.last_seen_at).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' })
                    : 'Never'
                  }`}
            </div>
          </div>
          {connectedDevices.length > 1 && (
            <span style={{
              background: '#e2e8f0', padding: '4px 10px', borderRadius: 99,
              fontSize: 11, fontWeight: 600, color: '#475569',
            }}>
              +{connectedDevices.length - 1} more
            </span>
          )}
        </div>
      ) : (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 14,
          padding: '16px 20px', marginBottom: 20, borderRadius: 14,
          background: '#f8fafc', border: '1.5px dashed #cbd5e1',
        }}>
          <div style={{
            width: 44, height: 44, borderRadius: 12,
            background: '#e2e8f0',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 20, flexShrink: 0,
          }}>💻</div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: '#475569' }}>
              No device connected
            </div>
            <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 2 }}>
              Generate a setup code below and install ParentsProtection on your child's Mac
            </div>
          </div>
        </div>
      )}

      {/* ── Phone-specific settings ───────────────────────────────────────── */}
      {deviceFilter === 'android' && (
        <>
          <Section title="📍 Location Tracking" accent="#10b981">
            <Toggle checked={true} onChange={() => {}} label="Enable GPS location tracking" />
            <div style={{ marginTop: 14 }}>
              <label>Tracking Interval</label>
              <div style={{ display: 'flex', gap: 6, marginTop: 4, flexWrap: 'wrap' }}>
                {[5, 10, 15, 30, 60].map(m => (
                  <button key={m} className="btn" style={{
                    background: m === 15 ? '#10b981' : '#e2e8f0',
                    color: m === 15 ? '#fff' : '#374151',
                    fontSize: 12, padding: '7px 14px', marginTop: 0,
                  }}>{m}m</button>
                ))}
              </div>
            </div>
          </Section>

          <Section title="📱 App Control" accent="#f59e0b">
            <p style={{ fontSize: 13, color: '#64748b', marginBottom: 12 }}>
              Block specific apps on your child's phone. Blocked apps will show an alert when opened.
            </p>
            <div style={{
              background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 10,
              padding: '12px 16px', fontSize: 12, color: '#92400e',
            }}>
              App blocking is configured through the Android app's device admin settings.
              The child won't be able to uninstall the monitoring app.
            </div>
          </Section>

          <Section title="🔕 Stealth Mode" accent="#6366f1">
            <Toggle checked={false} onChange={() => {}} label="Hide app from launcher" />
            <p style={{ fontSize: 11, color: '#94a3b8', marginTop: 6 }}>
              When enabled, the monitoring app won't appear in the app drawer.
              Notification will show as "System Service".
            </p>
          </Section>
        </>
      )}

      {/* ── PC-specific settings ───────────────────────────────────────────── */}
      {deviceFilter !== 'android' && (
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 18 }}>

        {/* LEFT: Screenshot Settings */}
        <Section title="📸 Screenshots" accent="#a855f7">
          <div style={{ marginBottom: 14 }}>
            <Toggle checked={screenshotEnabled} onChange={setScreenshotEnabled} label="Enable screenshots" />
          </div>
          <label>Capture Frequency</label>
          <div style={{ display: 'flex', gap: 6, marginTop: 4, flexWrap: 'wrap' }}>
            {SCREENSHOT_INTERVALS_PAID.map(i => {
              const isFreeLocked = !canAccessPaid && !SCREENSHOT_INTERVALS_FREE.includes(i);
              const lbl = i >= 60 ? `${i / 60}h` : `${i}m`;
              return (
                <div key={i} style={{ position: 'relative', flex: '1 1 auto', minWidth: 48 }}>
                  <button className="btn"
                    style={{
                      background: ssInterval === i ? '#0ea5e9' : isFreeLocked ? '#f1f5f9' : '#e2e8f0',
                      color: ssInterval === i ? '#fff' : isFreeLocked ? '#94a3b8' : '#374151',
                      marginTop: 0, fontSize: 12, padding: '7px 10px', width: '100%',
                      cursor: isFreeLocked ? 'not-allowed' : 'pointer',
                      opacity: isFreeLocked ? 0.6 : 1,
                    }}
                    onClick={() => {
                      if (isFreeLocked) { navigate('/dashboard/upgrade'); return; }
                      setSsInterval(i);
                    }}
                    title={isFreeLocked ? 'Upgrade to unlock faster screenshots' : ''}>
                    {isFreeLocked && <span style={{ marginRight: 4 }}>🔒</span>}
                    {lbl}
                  </button>
                  {isFreeLocked && (
                    <div style={{
                      position: 'absolute', top: -28, left: '50%', transform: 'translateX(-50%)',
                      background: '#1e293b', color: '#fff', fontSize: 10, padding: '3px 8px',
                      borderRadius: 4, whiteSpace: 'nowrap', pointerEvents: 'none',
                      opacity: 0, transition: 'opacity 0.2s',
                    }} className="upgrade-tooltip">Upgrade</div>
                  )}
                </div>
              );
            })}
          </div>
          <label style={{ display: 'block', marginTop: 14 }}>Screenshot Quality</label>
          <div style={{ display: 'flex', gap: 6, marginTop: 4 }}>
            {[
              { value: 'low', label: 'Low', desc: 'Saves data' },
              { value: 'medium', label: 'Medium', desc: 'Balanced' },
              { value: 'full', label: 'Full', desc: 'Best quality' },
            ].map(q => (
              <button key={q.value} className="btn"
                style={{
                  background: ssQuality === q.value ? '#0ea5e9' : '#e2e8f0',
                  color: ssQuality === q.value ? '#fff' : '#374151',
                  marginTop: 0, fontSize: 12, padding: '7px 10px', flex: 1,
                  flexDirection: 'column', display: 'flex', alignItems: 'center', gap: 2,
                }}
                onClick={() => setSsQuality(q.value)}>
                <span>{q.label}</span>
                <span style={{ fontSize: 9, opacity: 0.7 }}>{q.desc}</span>
              </button>
            ))}
          </div>
          <div style={{ marginTop: 14 }}>
            <Toggle checked={hideIcon} onChange={setHideIcon} label="Hide menu bar icon (stealth)" />
          </div>
        </Section>
      </div>
      )}

      {/* ── Screen Time — full width (shared PC + Phone) ──────────────────── */}
      <Section title="⏰ Screen Time" accent="#f59e0b">
        {!canAccessPaid ? (
          <LockedRow title="Screen Time Limits" desc="Set daily screen time limits and get alerts" navigate={navigate} />
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', gap: 20, flexWrap: 'wrap' }}>
            <div style={{ flex: 1, minWidth: 200 }}>
              <label>
                Daily Limit —{' '}
                <strong style={{ color: dailyLimit > 300 ? '#dc2626' : dailyLimit > 180 ? '#ca8a04' : '#16a34a' }}>
                  {limitLabel}
                </strong>
              </label>
              <input type="range" min={30} max={480} step={30}
                value={dailyLimit} onChange={e => setDailyLimit(Number(e.target.value))} />
              <div style={{ display: 'flex', justifyContent: 'space-between',
                fontSize: 10, color: '#94a3b8', marginTop: 2 }}>
                <span>30m</span><span>2h</span><span>4h</span><span>6h</span><span>8h</span>
              </div>
            </div>
            <div style={{
              background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 10,
              padding: '10px 16px', fontSize: 12, color: '#92400e', minWidth: 180,
            }}>
              Alert at 80% usage and when limit is reached
            </div>
          </div>
        )}
      </Section>

      {/* ── Bedtime + Lock — full width ────────────────────────────────────── */}
      <Section title="🌙 Bedtime Schedule" accent="#6366f1">
        {!canAccessPaid ? (
          <LockedRow title="Bedtime Control" desc="Alert when your child uses the computer after bedtime" navigate={navigate} />
        ) : (
          <>
            <div style={{ marginBottom: 14 }}>
              <Toggle checked={bedtimeEnabled} onChange={setBedtimeEnabled} label="Enable bedtime alerts" />
            </div>
            {bedtimeEnabled && (
              <>
                <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 14 }}>
                  <div style={{ flex: 1, minWidth: 150 }}>
                    <label>Bedtime</label>
                    <select value={bedtimeHour} onChange={e => setBedtimeHour(Number(e.target.value))}>
                      {Array.from({ length: 24 }, (_, i) => (
                        <option key={i} value={i}>{fmt(i)}</option>
                      ))}
                    </select>
                  </div>
                  <div style={{ flex: 1, minWidth: 150 }}>
                    <label>Wake Time</label>
                    <select value={waketimeHour} onChange={e => setWaketimeHour(Number(e.target.value))}>
                      {Array.from({ length: 24 }, (_, i) => (
                        <option key={i} value={i}>{fmt(i)}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div style={{
                  background: '#eef2ff', border: '1px solid #c7d2fe',
                  borderRadius: 10, padding: '10px 16px', fontSize: 13, color: '#4338ca',
                  marginBottom: 16,
                }}>
                  🌙 Screen blocked from <strong>{fmt(bedtimeHour)}</strong> to <strong>{fmt(waketimeHour)}</strong>
                </div>

                {/* ── Screen Lock Controls ──────────────────────────────── */}
                <div style={{
                  background: '#fafafa', borderRadius: 12, padding: '18px 20px',
                  border: '1px solid #e2e8f0',
                }}>
                  <div style={{ marginBottom: 14 }}>
                    <Toggle checked={lockScreenEnabled} onChange={setLockScreenEnabled}
                      label="Lock screen during bedtime" />
                    <p style={{ fontSize: 11, color: '#94a3b8', marginTop: 4, marginLeft: 54 }}>
                      Full-screen overlay blocks the computer during bedtime hours
                    </p>
                  </div>

                  {lockScreenEnabled && (
                    <>
                      <div style={{ display: 'flex', gap: 16, alignItems: 'flex-end', flexWrap: 'wrap', marginBottom: 14 }}>
                        <div>
                          <label style={{ fontSize: 12 }}>Screen Lock PIN</label>
                          <input type="text" value={bedtimePin} maxLength={4}
                            inputMode="numeric" pattern="[0-9]*"
                            onChange={e => setBedtimePin(e.target.value.replace(/\D/g, '').slice(0, 4))}
                            style={{
                              width: 100, textAlign: 'center', fontSize: 22, fontWeight: 700,
                              letterSpacing: 8, fontFamily: 'monospace',
                            }}
                            placeholder="1234" />
                        </div>
                        <span style={{ fontSize: 11, color: '#94a3b8', paddingBottom: 12 }}>
                          4-digit PIN to dismiss the lock screen
                        </span>
                      </div>

                      {lockActive && (
                        <div style={{
                          background: '#fef2f2', border: '1px solid #fecaca',
                          borderRadius: 10, padding: '10px 16px', fontSize: 13, color: '#dc2626',
                          marginBottom: 14, display: 'flex', alignItems: 'center', gap: 8,
                        }}>
                          <span style={{ fontSize: 18 }}>🔒</span>
                          <strong>Screen lock is active</strong> on the child's device right now
                        </div>
                      )}

                      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                        <button className="btn"
                          style={{ marginTop: 0, fontSize: 12, padding: '8px 16px', background: '#22c55e' }}
                          onClick={() => setUnlockUntil(480)}>
                          🔓 Unlock Now
                        </button>
                        <button className="btn"
                          style={{ marginTop: 0, fontSize: 12, padding: '8px 16px', background: '#0ea5e9' }}
                          onClick={() => setUnlockUntil(30)}>
                          ⏰ Allow 30 min
                        </button>
                        <button className="btn"
                          style={{ marginTop: 0, fontSize: 12, padding: '8px 16px', background: '#6366f1' }}
                          onClick={() => setUnlockUntil(60)}>
                          ⏰ Allow 60 min
                        </button>
                      </div>
                    </>
                  )}
                </div>
              </>
            )}
          </>
        )}
      </Section>

      {/* ── Two-column: Blocked Sites + Keywords ──────────────────────────── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 18 }}>
        <Section title="🚫 Blocked Websites" accent="#ef4444">
          <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
            <input type="text" value={newSite} placeholder="e.g. youtube.com"
              onChange={e => setNewSite(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && addSite()} style={{ flex: 1 }} />
            <button className="btn" style={{ marginTop: 0, padding: '9px 14px', fontSize: 12 }}
              onClick={addSite}>Add</button>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {blockedSites.length === 0
              ? <span style={{ fontSize: 12, color: '#94a3b8' }}>No blocked sites yet</span>
              : blockedSites.map(s => (
                <Pill key={s} label={s} color="#fee2e2"
                  onRemove={() => setBlockedSites(prev => prev.filter(x => x !== s))} />
              ))}
          </div>
        </Section>

        <Section title="🔤 Custom Keywords" accent="#f59e0b">
          <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
            <input type="text" value={newKeyword} placeholder="e.g. violence, weed"
              onChange={e => setNewKeyword(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && addKeyword()} style={{ flex: 1 }} />
            <button className="btn" style={{ marginTop: 0, padding: '9px 14px', fontSize: 12 }}
              onClick={addKeyword}>Add</button>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {customKeywords.length === 0
              ? <span style={{ fontSize: 12, color: '#94a3b8' }}>No custom keywords yet</span>
              : customKeywords.map(k => (
                <Pill key={k} label={k} color="#fef3c7"
                  onRemove={() => setCustomKeywords(prev => prev.filter(x => x !== k))} />
              ))}
          </div>
        </Section>
      </div>

      {/* ── Alerts ─────────────────────────────────────────────────────────── */}
      <Section title="📱 Alerts">
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 16 }}>
          <div>
            <label>Alert Email</label>
            <input type="email" value={alertEmail} placeholder="parent@email.com"
              onChange={e => setAlertEmail(e.target.value)} />
          </div>
          {!canAccessPaid ? (
            <LockedRow title="WhatsApp Alerts" desc="Get instant WhatsApp messages" navigate={navigate} />
          ) : (
            <div>
              <label>WhatsApp Number</label>
              <input type="tel" value={alertWhatsapp} placeholder="+91 98765 43210"
                onChange={e => setAlertWhatsapp(e.target.value)} />
            </div>
          )}
        </div>
      </Section>

      {/* ── Two-column: Data Management + System Info ─────────────────────── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 18 }}>
        <Section title="💾 Data Management" accent="#ef4444">
          {[
            { key: 'screenshots', label: '📸 Clear screenshots', color: '#ef4444' },
            { key: 'websites',    label: '🌐 Clear websites',    color: '#f97316' },
            { key: 'alerts',      label: '🔔 Clear alerts',      color: '#8b5cf6' },
          ].map(({ key, label, color }) => (
            <div key={key} style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              padding: '8px 0', borderBottom: '1px solid #f1f5f9',
            }}>
              <span style={{ fontSize: 13 }}>{label}</span>
              <button className="btn"
                style={{ background: color, marginTop: 0, fontSize: 11, padding: '5px 12px' }}
                onClick={() => setConfirmClear(key)}>Clear</button>
            </div>
          ))}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: 8 }}>
            <span style={{ fontSize: 13 }}>📄 Export all data</span>
            <button className="btn"
              style={{ background: '#0ea5e9', marginTop: 0, fontSize: 11, padding: '5px 12px' }}
              onClick={exportJSON}>Export</button>
          </div>
        </Section>

        <Section title="ℹ️ System Info" accent="#64748b">
          <table style={{ fontSize: 13 }}><tbody>
            <tr><td style={{ color: '#64748b', padding: '6px 0' }}>Agent version</td><td style={{ padding: '6px 0' }}>2.0.0</td></tr>
            <tr><td style={{ color: '#64748b', padding: '6px 0' }}>Dashboard version</td><td style={{ padding: '6px 0' }}>2.0.0</td></tr>
            <tr><td style={{ color: '#64748b', padding: '6px 0' }}>Current plan</td><td style={{ padding: '6px 0' }}><strong>{plan}</strong></td></tr>
            <tr><td style={{ color: '#64748b', padding: '6px 0' }}>Config file</td><td style={{ padding: '6px 0' }}>~/.parentprotection/.env</td></tr>
            <tr><td style={{ color: '#64748b', padding: '6px 0' }}>LaunchAgent</td><td style={{ padding: '6px 0' }}>com.parentprotection.monitor</td></tr>
          </tbody></table>
        </Section>
      </div>

      {/* Coming Soon Settings */}
      <div style={{
        background: '#fffbeb', border: '2px dashed #fde68a',
        borderRadius: 16, padding: '24px', marginTop: 20,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
          <span style={{ fontSize: 24 }}>🚀</span>
          <div>
            <div style={{ fontWeight: 800, fontSize: 16, color: '#92400e' }}>Coming Soon</div>
            <div style={{ fontSize: 12, color: '#b45309' }}>New features and settings on the way</div>
          </div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 10 }}>
          {[
            { icon: '📍', label: 'Location Tracking', desc: 'GPS tracking & safe zones' },
            { icon: '📵', label: 'App Blocking', desc: 'Block apps by category or name' },
            { icon: '🔐', label: 'Remote Lock', desc: 'Lock device from dashboard' },
            { icon: '📺', label: 'YouTube Monitoring', desc: 'Track video watch history' },
            { icon: '🗺️', label: 'Geofencing', desc: 'Set safe zone boundaries' },
            { icon: '💬', label: 'Social Media Alerts', desc: 'Monitor social app usage' },
            { icon: '🆘', label: 'SOS Button', desc: 'Emergency panic button for child' },
            { icon: '🪟', label: 'Windows Support', desc: 'Monitor Windows PCs' },
            { icon: '🤖', label: 'Android Support', desc: 'Monitor Android phones' },
            { icon: '📱', label: 'iOS Support', desc: 'Monitor iPhones & iPads' },
          ].map(f => (
            <div key={f.label} style={{
              background: 'rgba(255,255,255,0.7)', borderRadius: 10,
              padding: '12px 14px', display: 'flex', alignItems: 'center', gap: 10,
            }}>
              <span style={{ fontSize: 20 }}>{f.icon}</span>
              <div>
                <div style={{ fontWeight: 600, fontSize: 12, color: '#92400e' }}>{f.label}</div>
                <div style={{ fontSize: 11, color: '#b45309' }}>{f.desc}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ── Bottom save ────────────────────────────────────────────────────── */}
      <div style={{ textAlign: 'center', marginTop: 8, marginBottom: 32 }}>
        <button className="btn" onClick={save} disabled={saving}
          style={{ fontSize: 15, padding: '12px 40px', fontWeight: 700 }}>
          {saving ? 'Saving...' : 'Save Settings'}
        </button>
      </div>

      {/* ── Confirm clear dialog ───────────────────────────────────────────── */}
      {confirmClear && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: '#fff', borderRadius: 16, padding: 28,
            maxWidth: 360, width: '90%', boxShadow: '0 8px 40px rgba(0,0,0,0.2)' }}>
            <div style={{ fontSize: 20, fontWeight: 700, marginBottom: 8 }}>Are you sure?</div>
            <div style={{ fontSize: 13, color: '#64748b', marginBottom: 20 }}>
              This will permanently delete all {confirmClear} data. This action cannot be undone.
            </div>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button className="btn-sm" onClick={() => setConfirmClear(null)}>Cancel</button>
              <button className="btn"
                style={{ background: '#ef4444', marginTop: 0, fontSize: 13, padding: '8px 18px' }}
                onClick={() => clearData(confirmClear)}>Delete</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Toast ──────────────────────────────────────────────────────────── */}
      {toast && (
        <div style={{
          position: 'fixed', bottom: 24, right: 24, background: '#1e293b',
          color: '#fff', borderRadius: 12, padding: '12px 22px',
          fontSize: 14, fontWeight: 500, zIndex: 2000,
          boxShadow: '0 4px 20px rgba(0,0,0,0.25)',
          display: 'flex', alignItems: 'center', gap: 8,
        }}>
          {toast}
        </div>
      )}
    </div>
  );
}
